import { lazy, Suspense, useContext } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { RouteContentSkeleton } from '@components/auth-loading-skeleton';
import { CompanySettingsLayout } from '@components/company-settings-layout';
import { EventRouteWrapper } from '@components/event-route-wrapper/event-route-wrapper';
import { ProtectedComponent } from '@components/protected-component';
import { ReportPageSkeleton } from '@components/report-skeleton/report-skeleton';
import {
  ENABLED_ADD_VENUE,
  ENABLED_ANALYTICS_ROUTE,
  ENABLED_BOOKING_DETAILS_ROUTE,
  ENABLED_COMPANY_EVENT_DEFAULTS_ROUTE,
  ENABLED_COMPANY_INFO_ROUTE,
  ENABLED_COMPANY_OFFERS_ROUTE,
  ENABLED_COMPANY_WIDGET_ROUTE,
  ENABLED_CUSTOMERS_ROUTE,
  ENABLED_DUPLICATE_EVENT_ROUTE,
  ENABLED_EVENT_CREATE_INFO_ROUTE,
  ENABLED_EVENT_EDIT_DETAILS_ROUTE,
  ENABLED_EVENT_EDIT_INFO_ROUTE,
  ENABLED_EVENT_OFFERS_ROUTE,
  ENABLED_EVENT_PUBLISH_ROUTE,
  ENABLED_INSIGHTS_ROUTE,
  ENABLED_USERS_DETAIL_ROUTE,
  ENABLED_USERS_ROUTE,
} from '@constants/company-auth-permissions';
import {
  ADD_VENUE_ROUTE,
  ANALYTICS_ROUTE,
  BOOKING_DETAILS_HOLDERS_ROUTE,
  BOOKING_DETAILS_ROUTE,
  COMPANY_BILLING_PATH,
  COMPANY_EVENT_DEFAULTS_PATH,
  COMPANY_IMPORT_ROUTE,
  COMPANY_INFO_PATH,
  COMPANY_INTEGRATIONS_PATH,
  COMPANY_ROUTE,
  COMPANY_USERS_PATH,
  COMPANY_VENUES_PATH,
  COMPANY_WIDGET_PATH,
  CUSTOMERS_ROUTE,
  DUPLICATE_EVENT_INFO_ROUTE,
  EVENT_CREATE_INFO_ROUTE,
  EVENT_EDIT_DETAILS_ROUTE,
  EVENT_EDIT_INFO_ROUTE,
  EVENT_LIST,
  EVENT_PUBLISH_ROUTE,
  EVENT_REPORT_ROUTE,
  INFO_POINT_ROUTE,
  INSIGHTS_ROUTE,
  OFFERS_FLOORPLAN_ROUTE,
  OFFERS_ROUTE,
  PRESET_FLOORPLAN_ROUTE,
  PRESET_LIST_ROUTE,
  PROCESS_IMPORT_ROUTE,
  SELECT_VENUE_ROUTE,
  USER_SETTINGS_ROUTE,
  USERS_DETAIL_ROUTE,
} from '@constants/routes';
import { CompanyClubContext } from '@contexts/company-club/company-club-context';
import { ProtectedImportProcess } from '@pages/events/[companyId]/[clubSlug]/protected-import-process';
import { useFeatureFlag } from '@posthog-app/use-feature-flag';

import { EagerLayoutWithoutSidebar } from './pages/eager-layout-without-sidebar';
import { ReportRouteWrapper } from './pages/report-route-wrapper';

const SelectVenue = lazy(() => import('@pages/select-venue'));
const ReportPage = lazy(() => import('@pages/events/[companyId]/[clubSlug]/[eventId]/report'));
const InfoPoint = lazy(() => import('@pages/events/[companyId]/[clubSlug]/[eventId]/info-point'));
const CustomersPage = lazy(() => import('@pages/events/[companyId]/[clubSlug]/customers/customers'));
const BookingDetails = lazy(() => import('@pages/events/[companyId]/[clubSlug]/[eventId]/booking/[bookingId]'));
const BookingHolderDetails = lazy(() => import('@pages/events/[companyId]/[clubSlug]/[eventId]/booking/[code]'));
const EventDuplicateInfo = lazy(() => import('@pages/events/[companyId]/[clubSlug]/[eventId]/duplicate/info'));
const EventInfoDetails = lazy(() => import('@pages/events/[companyId]/[clubSlug]/[eventId]/info-detail'));
const EventEditInfo = lazy(() => import('@pages/events/[companyId]/[clubSlug]/[eventId]/info'));
const EventEditDetails = lazy(() => import('@pages/events/[companyId]/[clubSlug]/[eventId]/details'));
const EventCreateInfo = lazy(() => import('@pages/events/[companyId]/[clubSlug]/info'));
const EventOffers = lazy(() => import('@pages/events/[companyId]/[clubSlug]/[eventId]/offers'));
const EventOffersFloorplanPage = lazy(() => import('@pages/events/[companyId]/[clubSlug]/[eventId]/offers/floorplan'));
const EventPublish = lazy(() => import('@pages/events/[companyId]/[clubSlug]/[eventId]/publish'));
const EventList = lazy(() => import('@pages/events/[companyId]/[clubSlug]/list'));
const Analytics = lazy(() => import('@pages/events/[companyId]/[clubSlug]/analytics'));
const Insights = lazy(() => import('@pages/events/[companyId]/[clubSlug]/insights'));
const ListPresets = lazy(() => import('@pages/events/[companyId]/[clubSlug]/presets/list'));
const FloorplanPresetPage = lazy(() => import('@pages/events/[companyId]/[clubSlug]/presets/floorplan-preset'));
const Users = lazy(() => import('@pages/[companyId]/[clubSlug]/company/users'));
const UserDetail = lazy(() => import('@pages/[companyId]/[clubSlug]/company/users/[userId]'));
const UserSettings = lazy(() => import('@pages/user-settings'));
const CompanyInfo = lazy(() => import('@pages/[companyId]/[clubSlug]/company/info'));
const CompanyEventDefaults = lazy(() => import('@pages/[companyId]/[clubSlug]/company/event-defaults'));
const CompanyIntegrations = lazy(() => import('@pages/[companyId]/[clubSlug]/company/integrations'));
const CompanyWidget = lazy(() => import('@pages/[companyId]/[clubSlug]/company/widget'));
const CompanyBilling = lazy(() => import('@pages/[companyId]/[clubSlug]/company/billing'));
const CompanyVenues = lazy(() => import('@pages/[companyId]/[clubSlug]/company/venues'));
const AddVenue = lazy(() => import('@pages/[companyId]/[clubSlug]/add-venue'));
export const AppRoutes = () => {
  const { isDelayedDeliveryActive, isResaleSettingsActive, isVenueSettingsActive } = useContext(CompanyClubContext);
  const isEventDefaultsActive = isDelayedDeliveryActive || isResaleSettingsActive;
  const isFloorplanV2Enabled = useFeatureFlag('floorplan-interactive-editor');

  return (
    <Suspense fallback={<RouteContentSkeleton />}>
      <Routes>
        <Route path='/' element={<Navigate replace to={SELECT_VENUE_ROUTE} />} />
        <Route path={SELECT_VENUE_ROUTE} element={<SelectVenue />} />
        <Route
          path={USERS_DETAIL_ROUTE}
          element={
            <ProtectedComponent permissions={ENABLED_USERS_DETAIL_ROUTE} isRoute>
              <UserDetail />
            </ProtectedComponent>
          }
        />
        <Route path={USER_SETTINGS_ROUTE} element={<UserSettings />} />
        <Route
          path={PRESET_LIST_ROUTE}
          element={
            <ProtectedComponent permissions={ENABLED_COMPANY_OFFERS_ROUTE} isRoute>
              <ListPresets />
            </ProtectedComponent>
          }
        />
        {isFloorplanV2Enabled && (
          <Route
            path={PRESET_FLOORPLAN_ROUTE}
            element={
              <ProtectedComponent permissions={ENABLED_COMPANY_OFFERS_ROUTE} isRoute>
                <FloorplanPresetPage />
              </ProtectedComponent>
            }
          />
        )}
        <Route
          path={EVENT_CREATE_INFO_ROUTE}
          element={
            <ProtectedComponent permissions={ENABLED_EVENT_CREATE_INFO_ROUTE} isRoute>
              <EventCreateInfo />
            </ProtectedComponent>
          }
        />
        <Route
          path={EVENT_EDIT_INFO_ROUTE}
          element={
            <ProtectedComponent permissions={ENABLED_EVENT_EDIT_INFO_ROUTE} isRoute>
              <EventRouteWrapper publishedComponent={EventInfoDetails} draftComponent={EventEditInfo} />
            </ProtectedComponent>
          }
        />
        <Route
          path={EVENT_EDIT_DETAILS_ROUTE}
          element={
            <ProtectedComponent permissions={ENABLED_EVENT_EDIT_DETAILS_ROUTE} isRoute>
              <EventRouteWrapper publishedComponent={EventInfoDetails} draftComponent={EventEditDetails} />
            </ProtectedComponent>
          }
        />
        <Route
          path={INSIGHTS_ROUTE}
          element={
            <ProtectedComponent permissions={ENABLED_INSIGHTS_ROUTE} isRoute>
              <Insights />
            </ProtectedComponent>
          }
        />
        <Route
          path={ANALYTICS_ROUTE}
          element={
            <ProtectedComponent permissions={ENABLED_ANALYTICS_ROUTE} isRoute>
              <Analytics />
            </ProtectedComponent>
          }
        />
        <Route
          path={BOOKING_DETAILS_HOLDERS_ROUTE}
          element={
            <ProtectedComponent permissions={ENABLED_BOOKING_DETAILS_ROUTE} isRoute>
              <BookingHolderDetails />
            </ProtectedComponent>
          }
        />
        <Route
          path={BOOKING_DETAILS_ROUTE}
          element={
            <ProtectedComponent permissions={ENABLED_BOOKING_DETAILS_ROUTE} isRoute>
              <BookingDetails />
            </ProtectedComponent>
          }
        />
        <Route
          path={EVENT_REPORT_ROUTE}
          element={
            <EagerLayoutWithoutSidebar>
              <ReportRouteWrapper>
                <Suspense fallback={<ReportPageSkeleton />}>
                  <ReportPage />
                </Suspense>
              </ReportRouteWrapper>
            </EagerLayoutWithoutSidebar>
          }
        />
        <Route
          path={INFO_POINT_ROUTE}
          element={
            <EagerLayoutWithoutSidebar>
              <InfoPoint />
            </EagerLayoutWithoutSidebar>
          }
        />
        <Route path={EVENT_LIST} element={<EventList />} />

        <Route
          path={DUPLICATE_EVENT_INFO_ROUTE}
          element={
            <ProtectedComponent permissions={ENABLED_DUPLICATE_EVENT_ROUTE} isRoute>
              <EventDuplicateInfo />
            </ProtectedComponent>
          }
        />
        {isFloorplanV2Enabled && (
          <Route
            path={OFFERS_FLOORPLAN_ROUTE}
            element={
              <ProtectedComponent permissions={ENABLED_EVENT_OFFERS_ROUTE} isRoute>
                <EventOffersFloorplanPage />
              </ProtectedComponent>
            }
          />
        )}
        <Route
          path={OFFERS_ROUTE}
          element={
            <ProtectedComponent permissions={ENABLED_EVENT_OFFERS_ROUTE} isRoute>
              <EventOffers />
            </ProtectedComponent>
          }
        />
        <Route
          path={EVENT_PUBLISH_ROUTE}
          element={
            <ProtectedComponent permissions={ENABLED_EVENT_PUBLISH_ROUTE} isRoute>
              <EventPublish />
            </ProtectedComponent>
          }
        />
        <Route
          path={CUSTOMERS_ROUTE}
          element={
            <ProtectedComponent permissions={ENABLED_CUSTOMERS_ROUTE} isRoute>
              <CustomersPage />
            </ProtectedComponent>
          }
        />
        <Route path={COMPANY_ROUTE} element={<CompanySettingsLayout />}>
          <Route
            path={COMPANY_INFO_PATH}
            element={
              <ProtectedComponent permissions={ENABLED_COMPANY_INFO_ROUTE} checkEveryPermission={false} isRoute>
                <CompanyInfo />
              </ProtectedComponent>
            }
          />
          {isEventDefaultsActive && (
            <Route
              path={COMPANY_EVENT_DEFAULTS_PATH}
              element={
                <ProtectedComponent permissions={ENABLED_COMPANY_EVENT_DEFAULTS_ROUTE} isRoute>
                  <CompanyEventDefaults />
                </ProtectedComponent>
              }
            />
          )}
          <Route
            path={COMPANY_INTEGRATIONS_PATH}
            element={
              <ProtectedComponent permissions={ENABLED_COMPANY_INFO_ROUTE} checkEveryPermission={false} isRoute>
                <CompanyIntegrations />
              </ProtectedComponent>
            }
          />
          <Route
            path={COMPANY_WIDGET_PATH}
            element={
              <ProtectedComponent permissions={ENABLED_COMPANY_WIDGET_ROUTE} isRoute>
                <CompanyWidget />
              </ProtectedComponent>
            }
          />
          <Route
            path={COMPANY_BILLING_PATH}
            element={
              <ProtectedComponent permissions={ENABLED_COMPANY_INFO_ROUTE} checkEveryPermission={false} isRoute>
                <CompanyBilling />
              </ProtectedComponent>
            }
          />
          {isVenueSettingsActive && (
            <Route
              path={COMPANY_VENUES_PATH}
              element={
                <ProtectedComponent permissions={ENABLED_COMPANY_INFO_ROUTE} checkEveryPermission={false} isRoute>
                  <CompanyVenues />
                </ProtectedComponent>
              }
            />
          )}
          <Route
            path={COMPANY_USERS_PATH}
            element={
              <ProtectedComponent permissions={ENABLED_USERS_ROUTE} isRoute>
                <Users />
              </ProtectedComponent>
            }
          />
        </Route>
        <Route
          id='addVenue'
          path={ADD_VENUE_ROUTE}
          element={
            <ProtectedComponent permissions={ENABLED_ADD_VENUE} isRoute>
              <AddVenue />
            </ProtectedComponent>
          }
        />
        <Route id='processImport' path={PROCESS_IMPORT_ROUTE} element={<ProtectedImportProcess />} />
        <Route id='companyImport' path={COMPANY_IMPORT_ROUTE} element={<ProtectedImportProcess />} />
      </Routes>
    </Suspense>
  );
};
